import {
  CalendarEventGuest,
  CalendarEventResponseStatus,
  ExternalConnectionContact,
} from '@griply/types';
import {
  addCalendarEventGuest,
  CalendarEventGuestListEntry,
  filterContactSuggestions,
  getCalendarEventGuestStatusLabelKey,
  isValidEmail,
  removeCalendarEventGuest,
} from '@griply/utils';
import React, { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'shared/assets/icons';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';

import * as Styled from './form-guest-input.style';

const MAX_VISIBLE_GUESTS = 5;

export type FormGuestInputProps = {
  // Read-only callers pass the merged list from `getCalendarEventGuestList`, whose entries
  // carry `isOrganizer`; the editable form passes plain guests, which never do.
  value: (CalendarEventGuest | CalendarEventGuestListEntry)[];
  onChange?: (guests: CalendarEventGuest[]) => void;
  contacts?: ExternalConnectionContact[];
  placeholder?: string;
  error?: string;
  // an invitee cannot change the guest list — the rows render without input or remove buttons
  readOnly?: boolean;
};

export const FormGuestInput: React.FC<FormGuestInputProps> = ({
  value,
  onChange,
  contacts,
  placeholder,
  error,
  readOnly,
}) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  // a verdict on an address the user submitted — a half-typed one is not wrong yet
  const [showInvalid, setShowInvalid] = useState(false);

  const suggestions = useMemo(
    () =>
      filterContactSuggestions({
        contacts: contacts ?? [],
        draft,
        guests: value,
      }),
    [contacts, draft, value],
  );
  const suggestionsOpen = !suggestionsDismissed && !!suggestions.length;
  const activeSuggestionIndex = Math.min(activeIndex, suggestions.length - 1);

  const commitDraft = () => {
    if (!draft.trim() || !onChange) {
      return;
    }
    const next = addCalendarEventGuest(value, draft);
    if (next === value) {
      return;
    }
    onChange(next);
    setDraft('');
  };

  const selectSuggestion = (contact: ExternalConnectionContact) => {
    if (!onChange) {
      return;
    }
    onChange(addCalendarEventGuest(value, contact.email, contact.name));
    setDraft('');
    setActiveIndex(0);
  };

  const submitDraft = () => {
    const email = draft.trim();
    if (!email) {
      return;
    }
    if (!isValidEmail(email)) {
      setShowInvalid(true);
      return;
    }
    commitDraft();
  };

  const dismissSuggestions = () => setSuggestionsDismissed(true);

  const onInputChange = (next: string) => {
    setDraft(next);
    setActiveIndex(0);
    setSuggestionsDismissed(false);
    setShowInvalid(false);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      suggestionsOpen &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(
        (activeSuggestionIndex + delta + suggestions.length) %
          suggestions.length,
      );
      return;
    }

    if (event.key !== 'Enter' && event.key !== ',') {
      return;
    }
    event.preventDefault();

    if (event.key === 'Enter' && suggestionsOpen) {
      selectSuggestion(suggestions[activeSuggestionIndex]);
      return;
    }
    submitDraft();
  };

  const preventInputBlur = (event: React.MouseEvent) => event.preventDefault();

  const onExpand = () => setExpanded(true);

  const visibleGuests =
    expanded || value.length <= MAX_VISIBLE_GUESTS
      ? value
      : value.slice(0, MAX_VISIBLE_GUESTS);
  const hiddenCount = value.length - visibleGuests.length;

  return (
    <Styled.Container>
      {!!value.length && (
        <Styled.GuestList>
          {visibleGuests.map((guest) => {
            const onRemove = () =>
              onChange?.(removeCalendarEventGuest(value, guest.email));
            const isOrganizer = 'isOrganizer' in guest && guest.isOrganizer;
            // a provider that does not also list the organizer among the attendees records
            // no RSVP for them — the row still gets a dot so it lines up with the rest, but
            // an unrecorded answer is exactly what the neutral one means
            const status =
              guest.responseStatus ??
              (isOrganizer
                ? CalendarEventResponseStatus.NeedsAction
                : undefined);
            const statusLabel = status
              ? t(getCalendarEventGuestStatusLabelKey(status))
              : null;
            return (
              <Styled.GuestRow
                key={guest.email}
                title={guest.name ? guest.email : undefined}
              >
                {!!status && !!statusLabel && (
                  <Styled.StatusDot
                    $status={status}
                    role="img"
                    aria-label={statusLabel}
                    title={statusLabel}
                  />
                )}
                <Styled.GuestName>{guest.name || guest.email}</Styled.GuestName>
                {isOrganizer && (
                  <Styled.OrganizerBadge>
                    {t('forms.event.fields.guests.organizer')}
                  </Styled.OrganizerBadge>
                )}
                {!readOnly && (
                  <Styled.RemoveButton>
                    <IconButton
                      icon={X}
                      size={Sizes.Small}
                      color={Colors.Default}
                      type="button"
                      tabIndex={-1}
                      onClick={onRemove}
                      aria-label={guest.email}
                    />
                  </Styled.RemoveButton>
                )}
              </Styled.GuestRow>
            );
          })}

          {!!hiddenCount && (
            <Styled.MoreButton type="button" onClick={onExpand}>
              {t('forms.event.fields.guests.more', { count: hiddenCount })}
            </Styled.MoreButton>
          )}
        </Styled.GuestList>
      )}

      {!readOnly && (
        <>
          <Styled.InputAnchor ref={anchorRef}>
            <ResizableTextArea
              role="combobox"
              autoComplete="off"
              aria-expanded={suggestionsOpen}
              aria-controls={suggestionsOpen ? listboxId : undefined}
              aria-activedescendant={
                suggestionsOpen
                  ? `${listboxId}-option-${activeSuggestionIndex}`
                  : undefined
              }
              aria-autocomplete="list"
              value={draft}
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              onBlur={commitDraft}
              variant={ResizableTextAreaVariants.Body2}
              placeholder={placeholder}
              fullWidth
            />

            {suggestionsOpen && (
              <PopupMenu
                referenceElement={anchorRef}
                onOutside={dismissSuggestions}
                position="bottom"
                fallbackPositions={['top', 'bottom-start', 'top-start']}
                preventReferenceElementOverlap
                hasParentWidth
              >
                <PopupMenuList id={listboxId} role="listbox">
                  {suggestions.map((contact, index) => {
                    const handleSelect = () => selectSuggestion(contact);
                    return (
                      <PopupMenuListItem
                        key={contact.email}
                        role="presentation"
                      >
                        <PopupMenuButton
                          id={`${listboxId}-option-${index}`}
                          role="option"
                          aria-selected={index === activeSuggestionIndex}
                          isSelected={index === activeSuggestionIndex}
                          tabIndex={-1}
                          onClick={handleSelect}
                          onMouseDown={preventInputBlur}
                          subtitle={contact.name ? contact.email : undefined}
                          overflowEllipsis
                        >
                          {contact.name ?? contact.email}
                        </PopupMenuButton>
                      </PopupMenuListItem>
                    );
                  })}
                </PopupMenuList>
              </PopupMenu>
            )}
          </Styled.InputAnchor>

          {/* the borderless field has no red outline left to carry the invalid state */}
          {showInvalid && !!draft.trim() && !!error && (
            <Styled.ErrorText role="alert">{error}</Styled.ErrorText>
          )}
        </>
      )}
    </Styled.Container>
  );
};
